import { useCallback, useEffect, useRef, useState } from "react";
import { productDataAutoContinueDecision } from "./supplierOptionHelpers";

export const AUTO_CONTINUE_DELAY_MS = 1500;

export const useProductDataAutoContinue = ({ open, visible, result, submitted, polling, uploading, onContinue }) => {
  const [enabled, setEnabled] = useState(false);
  const [message, setMessage] = useState("");
  const enabledRef = useRef(false);
  const timer = useRef(null);
  const continued = useRef(new Set());
  const callback = useRef(onContinue);
  callback.current = onContinue;

  const stop = useCallback((reason = "") => {
    const wasEnabled = enabledRef.current;
    enabledRef.current = false;
    clearTimeout(timer.current);
    setEnabled(false);
    if (wasEnabled || !reason) setMessage(reason);
  }, []);

  const start = useCallback(value => {
    clearTimeout(timer.current);
    continued.current.clear();
    enabledRef.current = Boolean(value);
    setEnabled(Boolean(value));
    setMessage(value ? "Auto-continuing batches…" : "");
  }, []);

  useEffect(() => { if (!open) stop(); }, [open, stop]);
  useEffect(() => () => { enabledRef.current = false; clearTimeout(timer.current); }, []);
  useEffect(() => {
    if (!enabled || !open || !visible || polling || uploading) return undefined;
    const decision = productDataAutoContinueDecision({ result, submitted });
    if (decision.action === "stop") { stop(decision.message); return undefined; }
    if (decision.action !== "next") return undefined;
    const batchId = result?.batch?.id || result?.importBatch?.id || result?.importBatchId;
    if (continued.current.has(batchId)) return undefined;
    setMessage("Next batch will start automatically.");
    timer.current = setTimeout(() => {
      if (!enabledRef.current || continued.current.has(batchId)) return;
      continued.current.add(batchId);
      setMessage("Auto-continuing batches…");
      callback.current();
    }, AUTO_CONTINUE_DELAY_MS);
    return () => clearTimeout(timer.current);
  }, [enabled, open, visible, result, submitted, polling, uploading, stop]);

  return { enabled, message, start, stop };
};
